import { FC } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, styled } from '@mui/material';

interface BillingToggleProps {
  selectedFrequency: 'month' | 'year';
  onChange: (frequency: 'month' | 'year') => void;
  savingsPercent?: number;
}

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: '8px',
  padding: '3px',
  '& .MuiToggleButtonGroup-grouped': {
    border: 'none',
    borderRadius: '6px !important',
    padding: '6px 20px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    '&.Mui-selected': {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.text.primary,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      '&:hover': {
        backgroundColor: theme.palette.common.white,
      },
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

const BillingToggle: FC<BillingToggleProps> = ({ selectedFrequency, onChange, savingsPercent }) => {
  const handleChange = (_event: React.MouseEvent<HTMLElement>, newFrequency: 'month' | 'year' | null) => {
    if (newFrequency !== null) {
      onChange(newFrequency);
    }
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <StyledToggleButtonGroup
        value={selectedFrequency}
        exclusive
        onChange={handleChange}
        aria-label="billing frequency"
      >
        <ToggleButton value="month" aria-label="monthly billing">
          Monthly
        </ToggleButton>
        <ToggleButton value="year" aria-label="annual billing">
          Annually
        </ToggleButton>
      </StyledToggleButtonGroup>

      {savingsPercent != null && savingsPercent > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: -5,
            right: -17,
            bgcolor: 'secondary.main',
            color: 'secondary.contrastText',
            fontSize: '10px',
            fontWeight: 700,
            lineHeight: 1,
            px: 1,
            py: 0.4,
            borderRadius: '100px',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
          }}
        >
          Save {savingsPercent}%
        </Box>
      )}
    </Box>
  );
};

export default BillingToggle;
